import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData, ISpecSheet } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Reads and writes a single field on the Spec Sheet form in the React Query cache.
 * Finds the form by type === 'Spec Sheet' and propertyGuid.
 */
export function useSpecSheetField<T>(
    propertyGuid: string,
    selector: (specSheet: ISpecSheet) => T,
    updater: (specSheet: ISpecSheet, value: T) => ISpecSheet
): [T | undefined, (value: T) => void] {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<IApplicationData, Error, T | undefined>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: (d) => {
            const form = d.information?.properties?.forms?.find(
                (f) => f.type === 'Spec Sheet' && f.propertyGuid === propertyGuid
            ) as ISpecSheet | undefined;
            return form !== undefined ? selector(form) : undefined;
        },
    });

    const setValue = (value: T) => {
        if (entryGuid === undefined) return;
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid), (old) => {
            if (!old) return old;
            const forms = old.information?.properties?.forms ?? [];
            const idx = forms.findIndex(
                (f) => f.type === 'Spec Sheet' && f.propertyGuid === propertyGuid
            );
            if (idx === -1) return old;
            const form = forms[idx] as ISpecSheet;
            const newForms = [...forms];
            newForms[idx] = updater(form, value);
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        forms: newForms,
                    },
                },
            };
        });
    };

    return [currentValue, setValue];
}
